import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import HotTopicItem from './HotTopicItem';
import { usePrevious } from '../../../../utils/hooks';
import { isMobile, isIPad13 } from 'react-device-detect';
import Button from '@mui/material/Button';
import _ from 'lodash';
import { formatHotTopicContents } from '../../../../utils/hot-topic';
import animateScrollTo from 'animated-scroll-to';
import { changeLocationHash } from '../../../../utils/router';
import clsx from 'clsx';
import TagsRegion from './TagsRegion/TagsRegion';
import { LANGUAGE } from '../../../../constants/language';
import { translateMessage } from '../../../../utils/locale';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import HotTopicModeToggle from './HotTopicModeToggle';

const HIDDEN_STUDIO_TAG_LABELS = new Set(['簡體版', '简体版', 'Simplified Chinese']);

const WrapperStyle: React.CSSProperties = { padding: isMobile || isIPad13 ? '0px 5px' : '0 30px', textAlign: 'center' };
const CategoryWrapperStyles = (isLargeScreen: boolean): React.CSSProperties => ({
  display: 'flex',
  flexDirection: 'row',
  padding: isLargeScreen ? '0 15px' : '0'
});
const TitleStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  height: '20px'
};
const MobileStyle: React.CSSProperties = { display: 'flex', flexDirection: 'row', overflow: 'scroll' };
const RowStyle: React.CSSProperties = { marginTop: 24 };
const SeeAllStyle: React.CSSProperties = { color: '#9E9E9E', padding: '3px 6px 0px' };

const categories = ['saving', 'health', 'product-concept', 'misc', 'greeting-card', 'recruit-and-growth'];
const emptyData = categories.map(category => ({
  categoryId: category,
  categoryName: {
    en: translateMessage(category, 'en'),
    zh: translateMessage(category, 'zh'),
    'zh-Hant-HK': translateMessage(category, 'zh-Hant-HK')
  },
  items: [] as any[]
}));

const HotTopicContentSlides: React.FC<any> = ({
  intl,
  hotTopic,
  location,
  history,
  userDetails,
  subscriptions,
  className,
  SeeMoreProps,
  HotTopicItemProps,
  statistics,
  categoryViewPath,
  header,
  tagViewPath,
  cardRender = undefined,
  useCardRender = undefined,
  toggleDialogShowTags = false,
  mode = 'existing',
  onModeChange
}) => {
  const [contents, setContents] = useState<any[] | null>(null);
  const [taggedContents, setTaggedContents] = useState<any[]>([]);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 850);
  const [isIpadWidth, setIsIpadWidth] = useState(window.innerWidth > 767);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const previousHash = usePrevious(location.hash);
  const previousIsLargeScreen = usePrevious(isLargeScreen);
  const initialHashSet = useRef(false);
  /** The category already scrolled to, so one arrival scrolls once. */
  const scrolledToCategoryRef = useRef<string | null>(null);

  const sliderMapRef = useRef<Record<number, any>>({});
  const itemMapRef = useRef<Record<string, HTMLDivElement | null>>({});
  const hotTopicItemMapRef = useRef<Record<string, React.RefObject<any>>>({});

  const onToggleItem = (item: any, isOpen: boolean) => {
    if (mode === 'studio') {
      return;
    }
    if (isOpen) {
      changeLocationHash(item.id);
    } else {
      changeLocationHash('');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 767);
      setIsIpadWidth(window.innerWidth > 767);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const getContent = async () => {
      let items = _.get(hotTopic, 'data', []);

      if (mode !== 'studio') {
        items = items.filter((item: any) => !item.id?.startsWith('team-studio-'));
      }

      let data: any[] = JSON.parse(JSON.stringify(emptyData));

      let teamSpecificCategoryIds = items
        .map((item: any) => item.categoryId)
        .filter((categoryId: string) => categoryId.includes('team-specific'));
      let uniqueTeamSpecificCategoryIds = Array.from(new Set(teamSpecificCategoryIds)) as string[];
      for (let categoryId of uniqueTeamSpecificCategoryIds) {
        let teamSpecificItem = items.find((item: any) => item.categoryId === categoryId);
        data = [
          {
            categoryId,
            categoryName: {
              en: teamSpecificItem.categoryEn,
              zh: teamSpecificItem.categorySc,
              'zh-Hant-HK': teamSpecificItem.categoryTc
            },
            items: []
          },
          ...data
        ];
      }

      items.forEach((item: any) => {
        data.find(category => category.categoryId === item.categoryId)?.items.push(item);
      });

      const taggedContent = Object.values(
        items.reduce((acc: Record<string, any>, item: any) => {
          const tags = item?.tags || [];
          tags.forEach((tag: any) => {
            const tagId = tag?.[intl.locale || LANGUAGE.ZH_HANT_HK];
            if (acc[tagId]) {
              acc[tagId].items.push(item);
            } else {
              acc[tagId] = {
                tag: tag,
                items: [item]
              };
            }
          });
          return acc;
        }, {})
      );

      const featuredItems = items
        .filter((item: any) => item.isFeatured)
        .sort((a: any, b: any) => {
          if (b.createdAt && a.createdAt) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          } else if (!a.createdAt) {
            return 1;
          } else if (!b.createdAt) {
            return -1;
          } else {
            return -1;
          }
        })
        .map((item: any) => ({
          ...item,
          id: `featured-${item.id}`
        }));

      const dataWithFeatured =
        featuredItems.length > 0
          ? [
              {
                _id: 'featured',
                categoryName: {
                  en: '💎 Featured 💎',
                  'zh-Hant-HK': '💎 推介 💎',
                  zh: '💎 推介 💎'
                },
                items: featuredItems
              },
              ...data
            ]
          : data;

      const favouriteItems = items.filter(
        (item: any) => userDetails?.favouriteHotTopics && userDetails.favouriteHotTopics.includes(item.id)
      );

      const dataWithFeaturedAndFavourite =
        favouriteItems.length > 0
          ? [
              {
                _id: 'favourite',
                categoryName: {
                  en: '📌 Pinned',
                  'zh-Hant-HK': '📌 置頂',
                  zh: '📌 置顶'
                },
                items: favouriteItems
              },
              ...dataWithFeatured
            ]
          : dataWithFeatured;

      const formattedContents = formatHotTopicContents(dataWithFeaturedAndFavourite).filter(
        category => category.items && category.items.length > 0
      );

      const visibleTaggedContent =
        mode === 'studio'
          ? taggedContent.filter((content: any) => {
              const labels = Object.values((content && content.tag) || {});
              return !labels.some((label: any) => HIDDEN_STUDIO_TAG_LABELS.has(label as string));
            })
          : taggedContent;

      setContents(formattedContents);
      setTaggedContents(visibleTaggedContent);
    };

    if (hotTopic && hotTopic.data && !initialHashSet.current) {
      getContent().then().catch();
    }
  }, [hotTopic, location, userDetails, mode, intl.locale]);

  useEffect(() => {
    if (location.hash && contents) {
      if (previousHash !== location.hash || !initialHashSet.current) {
        initialHashSet.current = true;

        let targetTabIndex: number;
        let targetItemIndex: number | undefined;
        let selectedPanelId: string;

        selectedPanelId = location.hash.replace('#', '');
        targetTabIndex = contents.findIndex(category => {
          const index = category.items.findIndex((item: any) => item.id === selectedPanelId);
          if (index > -1) {
            targetItemIndex = index;
            return true;
          } else {
            return false;
          }
        });

        if (targetTabIndex < 0) {
          targetTabIndex = 0;
        }

        if (targetTabIndex > -1) {
          setTimeout(() => {
            const itemRef = itemMapRef.current[selectedPanelId];
            if (itemRef) {
              animateScrollTo(itemRef, { verticalOffset: -50 }).then(() => {});

              // need to scroll to the left if slider is not used
              if (isMobile || isIPad13) {
                const scrollParent = itemRef.parentElement;
                if (scrollParent) {
                  const boundingRect = itemRef.getBoundingClientRect();
                  scrollParent.scrollTo({
                    left: boundingRect.x,
                    behavior: 'smooth'
                  });
                }
              } else {
                const numSlides = isLargeScreen ? 5 : 3;
                const sliderRef = sliderMapRef.current[targetTabIndex];
                if (sliderRef) {
                  sliderRef.slickGoTo(Math.floor((targetItemIndex as number) / numSlides) * numSlides);
                }
              }
            }

            const hotTopicItemRef = hotTopicItemMapRef.current[selectedPanelId];
            if (hotTopicItemRef && hotTopicItemRef.current) {
              if (mode === 'studio') {
                const templateId = String(selectedPanelId || '').replace(/^featured-/, '');
                if (templateId) {
                  history.push(`/hot_topic_studio/${templateId}`);
                }
              } else {
                hotTopicItemRef.current.openDialog();
              }
            }

            // const targetTab = document.getElementById(`hot-topic-category-${targetTabIndex}`);
            // if (targetTab) {
            // 	// const topOffset = 0;
            // 	// const elementPosition = targetTab.getBoundingClientRect().top;
            // 	// const offsetPosition = elementPosition - topOffset;
            // 	animateScrollTo(targetTab)
            // 		.then()
            // 		.catch(console.log);
            // }
          }, 1);
        }
      }
    }
  }, [contents, isLargeScreen, location, previousHash, mode, history]);

  /**
   * Land on one category rather than at the top of the page.
   *
   * A caller that knows which row it wants sends `scrollToCategory` in the
   * route state — ClientV2's 招募 view does, for 招聘 & 成長, which is the LAST
   * of the six categories and therefore a screen and a half below the fold.
   * Without this the tile that promises recruitment content delivers the top of
   * a page about savings.
   *
   * The anchor is the id the category title already carries, so nothing new is
   * rendered for this; the index it is built from is the same one `contents`
   * is mapped with below.
   *
   * Once per arrival. The ref is what stops a later re-render — the store
   * filling in statistics, a tag being tapped — from yanking the page back to a
   * row the adviser has since scrolled away from.
   */
  useEffect(() => {
    const requested = (location?.state as { scrollToCategory?: string } | undefined)?.scrollToCategory;
    if (!requested || !contents || scrolledToCategoryRef.current === requested) return;
    const index = contents.findIndex(category => category.categoryId === requested);
    if (index < 0) return;
    scrolledToCategoryRef.current = requested;
    // One tick, for the same reason the hash effect above takes one: the rows
    // are still being laid out when this runs, and scrolling to an element
    // whose height is not settled lands short of it.
    setTimeout(() => {
      const target = document.getElementById(`hot-topic-category-${index}`);
      if (target) animateScrollTo(target, { verticalOffset: -70 }).catch(() => {});
    }, 1);
  }, [contents, location]);

  const settings = useMemo(
    () => ({
      dots: false,
      infinite: false,
      arrows: true,
      speed: 500,
      slidesToShow: isLargeScreen ? 5 : 3,
      slidesToScroll: isLargeScreen ? 5 : 3,
      swipe: false
    }),
    [isLargeScreen]
  );

  const stripHtml = useCallback((html: any) => (html ? String(html).replace(/<[^>]*>/g, '') : ''), []);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || !contents) return [];
    const query = searchQuery.toLowerCase();
    const seen = new Set<string>();
    const results: any[] = [];

    contents.forEach(category => {
      category.items?.forEach((item: any) => {
        const originalId = item.id?.startsWith('featured-') ? item.id.replace('featured-', '') : item.id;
        if (seen.has(originalId)) return;

        // Check title match in all languages
        let titleMatch = false;
        if (item.title) {
          titleMatch = Object.values(item.title).some((t: any) => t && String(t).toLowerCase().includes(query));
        }

        // Check texts match in all languages, capture the matched snippet
        let matchedText: string | null = null;
        if (item.texts) {
          for (const textArr of Object.values(item.texts) as any[]) {
            if (!Array.isArray(textArr)) continue;
            for (const textObj of textArr) {
              const subtitle = stripHtml(textObj?.subtitle);
              const description = stripHtml(textObj?.description);
              if (subtitle.toLowerCase().includes(query)) {
                matchedText = subtitle;
                break;
              }
              if (description.toLowerCase().includes(query)) {
                matchedText = description;
                break;
              }
            }
            if (matchedText) break;
          }
        }

        if (titleMatch || matchedText) {
          seen.add(originalId);
          // If title matched but no text matched, show first available text snippet
          let displayText = matchedText;
          if (titleMatch && !matchedText && item.texts) {
            const locale = intl.locale;
            const textArr = item.texts[locale] || Object.values(item.texts)[0];
            if (Array.isArray(textArr) && textArr[0]) {
              displayText = stripHtml(textArr[0].subtitle) || stripHtml(textArr[0].description) || '';
            }
          }
          results.push({ item, categoryName: category.categoryName, matchedText: displayText });
        }
      });
    });

    return results.slice(0, 20);
  }, [searchQuery, contents, stripHtml]);

  const onSearchResultClick = useCallback(
    (item: any) => {
      const originalId = item.id?.startsWith('featured-') ? item.id.replace('featured-', '') : item.id;
      setSearchQuery('');
      setSearchOpen(false);

      if (mode === 'studio') {
        if (originalId) {
          history.push(`/hot_topic_studio/${originalId}`);
        }
        return;
      }

      // Open the dialog directly via ref
      const hotTopicItemRef = hotTopicItemMapRef.current[originalId];
      if (hotTopicItemRef && hotTopicItemRef.current) {
        hotTopicItemRef.current.openDialog();
      }

      changeLocationHash(originalId);
    },
    [mode, history]
  );

  const onClickTag = ({ tag, items }: { tag: any; items: any[] }) => {
    history.push({
      pathname: tagViewPath,
      state: {
        tagViewPath: tagViewPath,
        taggedContents: taggedContents,
        tag: tag,
        contents: {
          categoryName: tag,
          items
        },
        useCardRender: useCardRender,
        ...SeeMoreProps,
        statistics: statistics,
        studioMode: mode === 'studio',
        // Keep location.state serializable (no functions) so history.push works.
        HotTopicItemProps: {
          enableLanguages: true,
          ...(mode === 'studio' ? { showStudioCover: true } : {})
        }
      }
    });
  };

  return (
    <Fragment>
      <div
        style={{
          padding: isMobile || isIPad13 ? '8px 5px' : '8px 30px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        {onModeChange && <HotTopicModeToggle mode={mode} onChange={onModeChange} />}
        <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
          <div style={{ position: 'relative', maxWidth: 400, width: '100%', flex: '1 1 240px' }}>
            <Paper
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '2px 8px',
                borderRadius: 8,
                backgroundColor: '#f5f5f5'
              }}
              elevation={0}
            >
              <SearchIcon style={{ color: '#9e9e9e', marginRight: 8 }} />
              <InputBase
                placeholder={intl.formatMessage({ id: 'Search' })}
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => {
                  if (searchQuery.trim()) setSearchOpen(true);
                }}
                style={{ flex: 1, fontSize: 14 }}
                fullWidth
              />
              {searchQuery && (
                <ClearIcon
                  style={{ color: '#9e9e9e', cursor: 'pointer', fontSize: 20 }}
                  onClick={() => {
                    setSearchQuery('');
                    setSearchOpen(false);
                  }}
                />
              )}
            </Paper>
            {searchOpen && searchResults.length > 0 && (
              <Paper
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1300,
                  maxHeight: 300,
                  overflowY: 'auto',
                  marginTop: 4,
                  borderRadius: 8
                }}
                elevation={3}
              >
                {searchResults.map(({ item, categoryName, matchedText }) => {
                  const title = item.title?.[intl.locale] || Object.values(item.title || {})[0] || '';
                  const category = categoryName?.[intl.locale] || '';

                  const highlightText = (text: string) => {
                    if (!text || !searchQuery.trim()) return text;
                    const idx = text.toLowerCase().indexOf(searchQuery.toLowerCase());
                    if (idx === -1) return text;
                    return (
                      <span>
                        {text.substring(0, idx)}
                        <span style={{ color: '#ff0000', fontWeight: 600 }}>
                          {text.substring(idx, idx + searchQuery.length)}
                        </span>
                        {text.substring(idx + searchQuery.length)}
                      </span>
                    );
                  };

                  return (
                    <div
                      key={item.id}
                      onClick={() => onSearchResultClick(item)}
                      style={{
                        padding: '10px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0
                          }}
                        >
                          {highlightText(title as string)}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: '#9e9e9e',
                            marginLeft: 12,
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                        >
                          {category}
                        </div>
                      </div>
                      {matchedText && (
                        <div
                          style={{
                            fontSize: 12,
                            color: '#666',
                            marginTop: 4,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {highlightText(matchedText)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </Paper>
            )}
          </div>
        </ClickAwayListener>
      </div>

      <TagsRegion taggedContents={taggedContents} onClickTag={onClickTag} />
      {header?.(onToggleItem)}
      <div style={WrapperStyle} className={clsx('hot-topic', className)}>
        {contents &&
          contents.map((category, index) => {
            const sliderRef = React.createRef();
            const isFeaturedCategory = category._id === 'featured';
            const items = category.items?.map((item: any, i: number) => {
              hotTopicItemMapRef.current[item.id] = React.createRef();
              const statisticId = item.id?.startsWith('featured-') ? item.id.replace('featured-', '') : item.id;
              return (
                <div
                  className={`hot-topic-item-${item.id}`}
                  key={`hot-topic-item-${item.id}`}
                  ref={ref => {
                    itemMapRef.current[item.id] = ref;
                  }}
                >
                  <HotTopicItem
                    cardRender={cardRender}
                    intl={intl}
                    itemData={item}
                    sliderRef={sliderRef}
                    index={i}
                    isLargeScreen={isLargeScreen}
                    previousIsLargeScreen={previousIsLargeScreen}
                    history={history}
                    userDetails={userDetails}
                    subscriptions={subscriptions}
                    isIpadWidth={isIpadWidth}
                    onToggleItem={onToggleItem}
                    shouldShowStatistic={!!statistics}
                    statistic={statistics ? statistics[statisticId] : undefined}
                    toggleDialogShowTags={toggleDialogShowTags}
                    {...HotTopicItemProps}
                    ref={hotTopicItemMapRef.current[item.id]}
                  />
                </div>
              );
            });
            return (
              <div key={`hot-topic-category-${index}`} style={RowStyle}>
                <div style={CategoryWrapperStyles(isLargeScreen)}>
                  <div style={TitleStyle}>
                    <div
                      id={`hot-topic-category-${index}`}
                      className="hot-topic-category-title"
                      style={isFeaturedCategory ? { color: '#ff0000' } : undefined}
                    >
                      {category.categoryName[intl.locale]} ({items.length})
                    </div>
                    <Button
                      onClick={() => {
                        history.push({
                          pathname: categoryViewPath,
                          state: {
                            useCardRender: useCardRender,
                            contents: category,
                            ...SeeMoreProps,
                            statistics: statistics,
                            studioMode: mode === 'studio',
                            tagViewPath: tagViewPath,
                            HotTopicItemProps: {
                              enableLanguages: true,
                              ...(mode === 'studio' ? { showStudioCover: true } : {})
                            }
                          }
                        });
                      }}
                      style={SeeAllStyle}
                    >
                      {intl.formatMessage({ id: 'See-All' })} ❯
                    </Button>
                  </div>
                </div>
                {isMobile || isIPad13 ? (
                  <div className="hot-topic-row-mobile" style={MobileStyle}>
                    {items}
                  </div>
                ) : (
                  <Slider {...settings} ref={(ref: Slider | null) => (sliderMapRef.current[index] = ref)}>
                    {items}
                  </Slider>
                )}
              </div>
            );
          })}
      </div>
    </Fragment>
  );
};

export default HotTopicContentSlides;
